import React from 'react';
import classNames from 'classnames';

// Section components
import SectionArticle from './SectionArticle';
import SectionCarousel from './SectionCarousel';
import SectionColumns from './SectionColumns';
import SectionFeatures from './SectionFeatures';
import SectionHero from './SectionHero';

// Styles
// Note: these contain
// - shared classes that are passed as defaultClasses
// - dark theme overrides
// TODO: alternatively, we could consider more in-place way of theming components
import css from './SectionBuilder.module.css';
import SectionFooter from './SectionFooter';
import {
  getCustomSectionClasses,
  getCustomSectionClassName,
} from '../../../extensions/Blogs/utils';
import SectionHeroCustom from '../../../extensions/LandingPage/components/SectionHeroCustom/SectionHeroCustom';
import SectionArticleCustom from '../../../extensions/LandingPage/components/SectionArticleCustom/SectionArticleCustom';
import SectionFounderCustom from '../../../extensions/LandingPage/components/SectionFounderCustom/SectionFounderCustom';

const MARKETPLACE_INTRODUCTION_ID = 'marketplace-introduction-section';
const DISCOVER_LOCAL_PRODUCERS_ID = 'discover-local-producers-section';
const BROWSE_MEMBERS_ID = 'browse-members-section';
const SLOGAN_ID = 'slogan-section';
const HOLIDAY_AT_BRITISH_ID = 'holiday-at-british-section';
const WHY_USE_PRODUCED_NEARBY_ID = 'why-use-produced-nearby-section';
const LOCAL_PRODUCER_GUIDE_ID = 'local-produce-guide-section';
const HELLO_FROM_FOUNDER_ID = 'hello-from-founder-section';
const HOW_IT_WORKS_ID = 'how-it-works-section';
const HERO_BANNER_ID = 'hero-banner-section';

// These are shared classes.
// Use these to have consistent styles between different section components
// E.g. share the same title styles
const DEFAULT_CLASSES = {
  sectionDetails: css.sectionDetails,
  title: css.title,
  description: css.description,
  ctaButton: css.ctaButton,
  blockContainer: css.blockContainer,
};

/////////////////////////////////////////////
// Mapping of section types and components //
/////////////////////////////////////////////

const defaultSectionComponents = {
  article: { component: SectionArticle },
  carousel: { component: SectionCarousel },
  columns: { component: SectionColumns },
  features: { component: SectionFeatures },
  footer: { component: SectionFooter },
  hero: { component: SectionHero },
};

const customSectionComponents = {
  [MARKETPLACE_INTRODUCTION_ID]: { component: SectionHeroCustom },
  [SLOGAN_ID]: { component: SectionArticleCustom },
  [HELLO_FROM_FOUNDER_ID]: { component: SectionFounderCustom },
};

const customCssClasses = {
  [DISCOVER_LOCAL_PRODUCERS_ID]: {
    sectionDetails: css.landingPageSectionDetails,
    title: css.landingPageTitle,
    carouselContainer: css.landingPageCarouselContainer,
    block: css.landingPageBlock,
  },
  [BROWSE_MEMBERS_ID]: {
    sectionDetails: css.landingPageSectionDetails,
    title: css.landingPageTitle,
    carouselContainer: css.landingPageCarouselContainer,
    block: css.landingPageBlock,
  },
  [SLOGAN_ID]: {
    sectionDetails: css.sloganSectionDetails,
  },
  [HOLIDAY_AT_BRITISH_ID]: {
    sectionDetails: css.landingPageSectionDetails,
    title: css.landingPageTitle,
    carouselContainer: css.landingPageCarouselContainer,
    block: css.landingPageBlock,
  },
  [WHY_USE_PRODUCED_NEARBY_ID]: {
    sectionDetails: css.landingPageSectionDetails,
    title: css.landingPageTitle,
    blockContainer: css.landingPageBlockContainer,
    block: css.whyUseNearbyBlock,
  },
  [LOCAL_PRODUCER_GUIDE_ID]: {
    sectionDetails: css.landingPageSectionDetails,
    title: css.landingPageTitle,
    carouselContainer: css.landingPageCarouselContainer,
    block: css.landingPageBlock,
  },
  [HELLO_FROM_FOUNDER_ID]: {
    blockContainer: css.helloFromFounderBlockContainer,
    title: css.helloFromFounderTitle,
    block: css.helloFromFounderBlock,
    ctaButton: css.helloFromFounderButton,
  },
  [HOW_IT_WORKS_ID]: {
    sectionDetails: css.landingPageSectionDetails,
    title: css.landingPageTitle,
    blockContainer: css.landingPageBlockContainer,
    block: css.howItWorksBlock,
    ctaButton: css.customButton,
  },
  [HERO_BANNER_ID]: {
    root: css.bannerDetails,
  }
};

const getSectionCustomClasses = sectionId => {
  return customCssClasses[sectionId] || {};
};

//////////////////////
// Section builder //
//////////////////////

/**
 * @typedef {Object} FieldOption
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * @typedef {Object} BlockOption
 * @property {ReactNode} component
 */

/**
 * @typedef {Object} SectionOption
 * @property {ReactNode} component
 */

/**
 * @typedef {Object} SectionConfig
 * @property {string} sectionId
 * @property {string} sectionName
 * @property {('article' | 'carousel' | 'columns' | 'features' | 'hero')} sectionType
 */

/**
 * Build section elements from given section config array.
 *
 * @component
 * @param {Object} props
 * @param {Array<SectionConfig>} props.sections
 * @param {Object} props.options
 * @param {Object<string,FieldOption>} props.options.fieldComponents
 * @param {Object<string,BlockOption>} props.options.blockComponents
 * @param {Object<string,SectionOption>} props.options.sectionComponents
 * @param {boolean} props.options.isInsideContainer
 * @returns {JSX.Element} element containing array of sections according from given config array.
 */
const SectionBuilder = props => {
  const { sections = [], options } = props;
  const { sectionComponents = {}, isInsideContainer, ...otherOption } = options || {};

  // If there's no sections, we can't render the correct section component
  if (!sections || sections.length === 0) {
    return null;
  }

  // Selection of Section components
  const components = { ...defaultSectionComponents, ...sectionComponents };
  const getComponent = (sectionType, sectionId) => {
    const config = customSectionComponents[sectionId] || components[sectionType]; // Custom section components take priority
    return config?.component;
  };

  // Generate unique ids for sections if operator has managed to create duplicates
  // E.g. "foobar", "foobar1", and "foobar2"
  const sectionIds = [];
  const getUniqueSectionId = (sectionId, index) => {
    const candidate = sectionId || `section-${index + 1}`;
    if (sectionIds.includes(candidate)) {
      let sequentialCandidate = `${candidate}1`;
      for (let i = 2; sectionIds.includes(sequentialCandidate); i++) {
        sequentialCandidate = `${candidate}${i}`;
      }
      return getUniqueSectionId(sequentialCandidate, index);
    } else {
      sectionIds.push(candidate);
      return candidate;
    }
  };

  return (
    <>
      {sections.map((section, index) => {
        const customSectionClasses = getCustomSectionClasses(section, DEFAULT_CLASSES);
        const customSectionClassName = getCustomSectionClassName(section);
        // If the default "dark" theme should be applied (when text color is white).
        // By default, this information is stored to customAppearance field
        const isDarkTheme =
          section?.appearance?.fieldType === 'customAppearance' &&
          section?.appearance?.textColor === 'white';
        const classes = classNames(customSectionClassName, { [css.darkTheme]: isDarkTheme });
        const sectionId = getUniqueSectionId(section.sectionId, index);
        const Section = getComponent(section.sectionType, sectionId);
        const customClasses = getSectionCustomClasses(sectionId);


        if (Section) {
          return (
            <Section
              key={`${sectionId}_i${index}`}
              className={classes}
              defaultClasses={customSectionClasses}
              isInsideContainer={isInsideContainer}
              options={otherOption}
              customClasses={customClasses}
              {...section}
              sectionId={sectionId}
            />
          );
        } else {
          // If the section type is unknown, the app can't know what to render
          console.warn(
            `Unknown section type (${section.sectionType}) detected using sectionName (${section.sectionName}).`
          );
          return null;
        }
      })}
    </>
  );
};

export default SectionBuilder;
